import React, { useMemo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

const CONFETTI_N = 48;
const COLS = ['#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#ef4444', '#06b6d4', '#eab308'];

/**
 * Confettis animés (Framer) + GIF optionnel : placez un fichier /public/submission-confetti.gif
 * pour l’illustration ; sinon seul l’effet de particules s’affiche.
 */
const SubmissionSuccessOverlay = ({
  open,
  onContinue,
  autoRedirectMs = 5000,
  redirectPath = '/dentiste',
  message = 'Votre soumission a bien été reçue. Nous accusons réception de votre dossier.',
}) => {
  const [gifOk, setGifOk] = useState(true);
  const pieces = useMemo(
    () =>
      Array.from({ length: CONFETTI_N }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 2.2 + Math.random() * 1.8,
        color: COLS[i % COLS.length],
        w: 5 + Math.random() * 8,
        h: 7 + Math.random() * 14,
        rot: Math.random() * 180,
      })),
    [open]
  );

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => onContinue(redirectPath), autoRedirectMs);
    return () => clearTimeout(t);
  }, [open, autoRedirectMs, redirectPath, onContinue]);

  if (!open) return null;

  const node = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="submission-success-title"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {pieces.map((p) => (
          <motion.div
            key={p.id}
            className="absolute rounded-sm shadow-sm"
            style={{
              left: `${p.x}%`,
              top: '-4%',
              width: p.w,
              height: p.h,
              backgroundColor: p.color,
            }}
            initial={{ y: 0, opacity: 0.95, rotate: p.rot }}
            animate={{ y: '115vh', opacity: 0.75, rotate: p.rot + 360 * 4 }}
            transition={{
              duration: p.duration,
              delay: p.delay,
              ease: 'linear',
              repeat: Infinity,
              repeatDelay: 0.15,
            }}
          />
        ))}
      </div>

      <motion.div
        className="relative z-10 w-full max-w-md rounded-2xl border-2 border-slate-200 bg-white p-6 sm:p-8 text-center text-slate-900 shadow-2xl dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 24, stiffness: 320 }}
      >
        {gifOk && (
          <img
            src="/submission-confetti.gif"
            alt=""
            className="w-40 h-40 sm:w-48 sm:h-48 mx-auto object-contain select-none"
            onError={() => setGifOk(false)}
          />
        )}

        <h2
          id="submission-success-title"
          className="text-xl sm:text-2xl font-semibold text-foreground mt-2"
        >
          C’est noté !
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">{message}</p>
        <p className="text-xs text-muted-foreground mt-4">
          Redirection automatique vers l’espace dentiste dans quelques secondes.
        </p>
        <div className="mt-6">
          <Button
            type="button"
            fullWidth
            onClick={() => onContinue(redirectPath)}
            iconName="LayoutGrid"
          >
            Aller à l’espace dentiste
          </Button>
        </div>
      </motion.div>
    </div>
  );

  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
};

export default SubmissionSuccessOverlay;
